import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Plus, Wand2, RotateCcw, Download, Share2, Loader2 } from "lucide-react";
import CarRender from "@/components/CarRender";
import QuoteDialog from "@/components/QuoteDialog";
import { Input } from "@/components/ui/input";
import { VEHICLES, FINISHES, COLORS, ACCENTS } from "@/data/catalog";
import { composeBuildImage } from "@/lib/buildImage";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const SectionLabel = ({ n, children }) => (
  <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">
    <span className="text-steel mr-2">{n}</span>
    {children}
  </p>
);

const Customizer = () => {
  const [vehicleId, setVehicleId] = useState("gwagon");
  const [finishId, setFinishId] = useState("satin");
  const [colorId, setColorId] = useState("vampire_red");
  const [accents, setAccents] = useState(["calipers_red"]);
  const [overlayText, setOverlayText] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildImage, setBuildImage] = useState(null);

  const openQuote = () => {
    setQuoteOpen(true);
    setBuildImage(null);
    composeBuildImage({ vehicle, color, finish: finishId, accents, overlayText })
      .then((canvas) => {
        const small = document.createElement("canvas");
        small.width = 1280;
        small.height = 720;
        small.getContext("2d").drawImage(canvas, 0, 0, 1280, 720);
        setBuildImage(small.toDataURL("image/jpeg", 0.85));
      })
      .catch(() => {});
  };

  const downloadBuild = async (share) => {
    setBuilding(true);
    try {
      const canvas = await composeBuildImage({ vehicle, color, finish: finishId, accents, overlayText });
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      const file = new File([blob], `g5-${vehicle.id}-build.png`, { type: "image/png" });
      if (share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My G5 Wrap Build" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        if (share) toast.success("Sharing isn't supported here — image downloaded instead");
      }
    } catch (e) {
      toast.error("Could not render the build image");
    } finally {
      setBuilding(false);
    }
  };

  const vehicle = VEHICLES.find((v) => v.id === vehicleId);
  const finish = FINISHES.find((f) => f.id === finishId);
  const color = COLORS.find((c) => c.id === colorId);

  useEffect(() => {
    VEHICLES.forEach((v) => {
      [v.img, v.mask, v.hoodMask, v.roofMask, v.trimMask].filter(Boolean).forEach((src) => {
        const i = new Image();
        i.src = src;
      });
    });
  }, []);

  const price = useMemo(() => {
    const addons = ACCENTS.filter((a) => accents.includes(a.id)).reduce((s, a) => s + a.price, 0);
    return vehicle.base * finish.multiplier + addons;
  }, [vehicle, finish, accents]);

  const toggleAccent = (id) =>
    setAccents((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const reset = () => {
    setFinishId("satin");
    setColorId("vampire_red");
    setAccents(["calipers_red"]);
    setOverlayText("");
  };

  const preset = {
    source: "wrap_studio",
    service: "Full Wrap",
    vehicle_type: vehicle.name,
    wrap_specifications: {
      finish: finish.name,
      color_name: color.name,
      color_hex: color.hex,
      accents: ACCENTS.filter((a) => accents.includes(a.id)).map((a) => a.name),
      overlay_text: overlayText,
      estimated_price: Math.round(price),
    },
    buildImage,
    summary: `VEHICLE — ${vehicle.name}\nFINISH — ${finish.name}\nCOLOR — ${color.name} (${color.hex})\nACCENTS — ${
      ACCENTS.filter((a) => accents.includes(a.id)).map((a) => a.name).join(", ") || "None"
    }${overlayText ? `\nDOOR TEXT — "${overlayText}"` : ""}\nESTIMATE — ${money(price)}`,
  };

  return (
    <main data-testid="customizer-view" className="pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">
            G5 Interactive Studio
          </p>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-6xl leading-none">
            Create Your <span className="text-velocity">Own Wrap.</span>
          </h1>
          <p className="mt-4 max-w-xl text-slate-400 leading-relaxed">
            Pick a platform, choose a finish and color, stack accent work — the blueprint renders live. Send the build
            to the studio when it looks right.
          </p>
        </motion.div>

        <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* CANVAS */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="relative rounded-2xl border border-edge bg-gunmetal carbon-bg blueprint-grid overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 h-40 w-3/4 bg-white/5 blur-3xl rounded-full" />
                <div className="flex items-center justify-between px-5 py-3 border-b border-edge/70">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">
                    Live Blueprint · Side Elevation
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase">
                    {finish.name} / {color.name}
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={vehicleId}
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -60 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <CarRender
                      vehicle={vehicle}
                      color={color}
                      finish={finishId}
                      accents={accents}
                      overlayText={overlayText}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-4 rounded-2xl border border-velocity/40 bg-panel p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 shadow-[0_0_30px_rgba(255,42,59,0.12)]">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">Estimated Build</p>
                  <p data-testid="customizer-price-estimate" className="font-display font-black text-2xl sm:text-3xl text-white">
                    {money(price)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Ballpark — final written quote after vehicle survey.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="customizer-reset-btn"
                    onClick={reset}
                    className="inline-flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 rounded-full border border-edge text-slate-300 text-xs sm:text-sm font-medium hover:border-steel transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" /> Reset
                  </button>
                  <button
                    data-testid="customizer-download-build-btn"
                    onClick={() => downloadBuild(false)}
                    disabled={building}
                    className="inline-flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 rounded-full border border-edge text-slate-300 text-xs sm:text-xs sm:text-sm font-medium hover:border-steel transition-colors disabled:opacity-50"
                  >
                    {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
                  </button>
                  <button
                    data-testid="customizer-share-build-btn"
                    onClick={() => downloadBuild(true)}
                    disabled={building}
                    className="inline-flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 rounded-full border border-edge text-slate-300 text-xs sm:text-xs sm:text-sm font-medium hover:border-steel transition-colors disabled:opacity-50"
                  >
                    <Share2 className="h-4 w-4" /> Share
                  </button>
                  <button
                    data-testid="customizer-submit-quote-btn"
                    onClick={openQuote}
                    className="inline-flex items-center gap-2 bg-velocity hover:bg-velocitydark text-white text-xs sm:text-sm font-semibold px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all duration-200 shadow-[0_0_24px_rgba(255,42,59,0.4)] hover:-translate-y-0.5"
                  >
                    <Wand2 className="h-4 w-4" /> Request Quote With This Build
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* CONTROLS */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <section>
              <SectionLabel n="01">Platform</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                {VEHICLES.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`customizer-vehicle-select-${v.id}`}
                    onClick={() => setVehicleId(v.id)}
                    className={`text-left rounded-xl border p-3 sm:p-4 transition-all duration-200 ${
                      vehicleId === v.id
                        ? "border-velocity bg-velocity/10 shadow-[0_0_18px_rgba(255,42,59,0.15)]"
                        : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <p className="font-display font-bold text-sm tracking-tight leading-tight">{v.name}</p>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">{v.desc}</p>
                    <p className="font-mono text-[10px] text-velocity mt-2">FROM {money(v.base)}</p>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <SectionLabel n="02">Film Finish</SectionLabel>
              <div className="space-y-2">
                {FINISHES.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`customizer-finish-select-${f.id}`}
                    onClick={() => setFinishId(f.id)}
                    className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 transition-all duration-200 ${
                      finishId === f.id ? "border-velocity bg-velocity/10" : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <span className="text-left">
                      <span className="block font-display font-bold text-sm tracking-tight">{f.name}</span>
                      <span className="block text-[11px] text-slate-500">{f.note}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-steel">×{f.multiplier.toFixed(2)}</span>
                      {finishId === f.id && <Check className="h-4 w-4 text-velocity" />}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <SectionLabel n="03">Color Library</SectionLabel>
              <div className="grid grid-cols-5 gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`customizer-color-swatch-${c.id}`}
                    onClick={() => setColorId(c.id)}
                    title={c.name}
                    aria-label={c.name}
                    className={`relative aspect-square rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                      colorId === c.id ? "border-velocity shadow-[0_0_16px_rgba(255,42,59,0.4)]" : "border-edge"
                    }`}
                    style={{ background: `linear-gradient(135deg, ${c.accent} 0%, ${c.hex} 55%, ${c.hex} 100%)` }}
                  >
                    {colorId === c.id && (
                      <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" />
                    )}
                  </button>
                ))}
              </div>
              <p data-testid="customizer-color-name" className="mt-3 font-mono text-xs text-slate-400 uppercase tracking-wider">
                {color.name} · {color.hex}
              </p>
            </section>

            <section>
              <SectionLabel n="04">Accent Work</SectionLabel>
              <div className="space-y-2">
                {ACCENTS.map((a) => {
                  const active = accents.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`customizer-accent-toggle-${a.id}`}
                      onClick={() => toggleAccent(a.id)}
                      className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 transition-all duration-200 ${
                        active ? "border-velocity bg-velocity/10" : "border-edge bg-panel hover:border-steel"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`h-5 w-5 rounded-md border flex items-center justify-center transition-colors ${
                            active ? "bg-velocity border-velocity" : "border-edge"
                          }`}
                        >
                          {active ? <Check className="h-3.5 w-3.5 text-white" /> : <Plus className="h-3.5 w-3.5 text-slate-500" />}
                        </span>
                        <span className="font-display font-bold text-sm tracking-tight">{a.name}</span>
                      </span>
                      <span className="font-mono text-xs text-steel">+{money(a.price)}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <SectionLabel n="05">Door Lettering (Optional)</SectionLabel>
              <Input
                data-testid="customizer-overlay-text-input"
                value={overlayText}
                maxLength={14}
                onChange={(e) => setOverlayText(e.target.value)}
                placeholder="e.g. G5 RACING or 07"
                className="bg-panel border-edge uppercase"
              />
              <p className="mt-2 text-[11px] text-slate-500">Rendered live on the door — vinyl lettering quoted separately.</p>
            </section>
          </div>
        </div>
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} preset={preset} />
      <section data-testid="custom-wrap-studio" className="max-w-3xl mx-auto px-4 sm:px-8 mt-12 sm:mt-16 border-t border-edge pt-8 sm:pt-10">
        <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-4">Design a Custom Vinyl Wrap Online in Springdale, AR</h2>
        <div className="space-y-4 text-sm text-slate-400 leading-relaxed">
          <p>G5 Auto Restyling built this wrap studio so you can explore real options before you ever call a shop. Choose your platform — from a Porsche 911 S and Mustang Dark Horse to a Ram 1500 TRX or Mercedes-AMG G 63 — then preview matte, satin and high-gloss finishes across a curated library of wrap colors inspired by the films we install every week, including Avery Dennison, 3M, Hexis and KPMF cast vinyl. Add accent work like a satin carbon hood, gloss black roof and pillars, full chrome delete, painted brake calipers or a ceramic over-coat, and watch the estimate update in real time.</p>
          <p>Every vehicle wrap we install in Northwest Arkansas starts the same way: full paint decontamination, careful disassembly, and hand-laid panels with tucked and sealed edges — never cut on your car. When your build looks right, send it straight to our Springdale studio and you'll receive a written quote with film availability and the next install window within 48 hours. Drivers visit us from Rogers, Bentonville, Fayetteville and across the region for color-change wraps, paint protection film and ceramic coating that hold up to Arkansas heat, storms and highway miles.</p>
        </div>
      </section>

    </main>
  );
};

export default Customizer;
